import { ArrowRight, KeyRound, ShieldCheck } from "lucide-react";
import QRCode from "qrcode";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, describeApiError, setMfaChallengeListener } from "../lib/api";
import { AuthLayout, FormError } from "../pages/AuthPages";
import { BrandSplash } from "./BrandSplash";
import { ButtonLabel } from "./ButtonLabel";

interface MfaStatus { required: boolean; enrolled: boolean; sessionVerified: boolean; recoveryCodesRemaining: number }

type Stage =
  | { kind: "loading" }
  | { kind: "open" }
  | { kind: "enrol" }
  | { kind: "codes"; codes: string[] }
  | { kind: "verify" };

/**
 * Staff and lending-partner screens sit behind an authenticator code (Kuspit security assessment
 * Q5a). The API enforces it on every privileged route; this gate only decides what to show: setup
 * with a QR code the first time, the six-digit prompt on each new session, and the one-time
 * recovery codes right after setup. A verification that lapses while a page is open (the API
 * answers MFA_REQUIRED) brings the prompt back.
 */
export function StaffMfaGate({ children, kicker }: { children: ReactNode; kicker: string }) {
  const { user, logout } = useAuth();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiRequest<{ data: MfaStatus }>("/api/v1/auth/mfa");
      if (!data.required || data.sessionVerified) setStage({ kind: "open" });
      else setStage(data.enrolled ? { kind: "verify" } : { kind: "enrol" });
    } catch {
      // The API stays the gatekeeper; show the screens and let them report their own errors.
      setStage({ kind: "open" });
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh, user?.id]);
  useEffect(() => setMfaChallengeListener((code) => {
    setStage((current) => current.kind === "codes" ? current : code === "MFA_ENROLLMENT_REQUIRED" ? { kind: "enrol" } : { kind: "verify" });
  }), []);

  if (stage.kind === "loading") return <BrandSplash />;
  if (stage.kind === "open") return <>{children}</>;

  const signOut = <button type="button" className="secondary-button full" onClick={() => void logout()}>Cerrar sesión</button>;
  return (
    <AuthLayout kicker={kicker} headline="Verificación en dos pasos" promise="El acceso a la administración y al portal de aliados requiere tu contraseña y un código de tu aplicación autenticadora.">
      {stage.kind === "enrol" && <EnrolForm email={user?.email ?? ""} onDone={(codes) => setStage({ kind: "codes", codes })} footer={signOut} />}
      {stage.kind === "codes" && <RecoveryCodes codes={stage.codes} onContinue={() => setStage({ kind: "open" })} />}
      {stage.kind === "verify" && <VerifyForm email={user?.email ?? ""} onDone={() => setStage({ kind: "open" })} footer={signOut} />}
    </AuthLayout>
  );
}

function CodeInput({ id, value, onChange, label }: { id: string; value: string; onChange(value: string): void; label: string }) {
  return (
    <div className="field-block">
      <div className="label-row"><label htmlFor={id}>{label}</label></div>
      <div className="auth-input-wrap">
        <KeyRound size={16} aria-hidden="true" />
        <input
          id={id} value={value} onChange={(event) => onChange(event.target.value)}
          inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus
          translate="no"
        />
      </div>
    </div>
  );
}

function EnrolForm({ email, onDone, footer }: { email: string; onDone(codes: string[]): void; footer: ReactNode }) {
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<{ data: { secret: string; otpauthUri: string } }>("/api/v1/auth/mfa/enroll", { method: "POST" })
      .then(async ({ data }) => {
        const image = await QRCode.toDataURL(data.otpauthUri, { margin: 1, width: 200, errorCorrectionLevel: "M" });
        if (!cancelled) { setSecret(data.secret); setQr(image); }
      })
      .catch((cause) => { if (!cancelled) setError(describeApiError(cause, "No pudimos iniciar la configuración.")); });
    return () => { cancelled = true; };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const { data } = await apiRequest<{ data: { recoveryCodes: string[] } }>("/api/v1/auth/mfa/enroll/confirm", {
        method: "POST", body: JSON.stringify({ code: code.replace(/\s/g, "") })
      });
      onDone(data.recoveryCodes);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos confirmar el código."));
    } finally { setBusy(false); }
  };

  return (
    <form className="auth-form" onSubmit={submit}>
      <h2 className="partner-login-title">Configura tu autenticador</h2>
      <p className="field-hint">
        Escanea el código con Google Authenticator, Microsoft Authenticator, 1Password u otra aplicación compatible, para la cuenta <strong translate="no">{email}</strong>.
      </p>
      <div className="mfa-qr">{qr ? <img src={qr} alt="Código QR para tu aplicación autenticadora" width={200} height={200} /> : <span className="field-hint">Generando código…</span>}</div>
      {secret && <p className="field-hint">¿No puedes escanear? Escribe esta clave: <code className="mfa-secret" translate="no">{secret.replace(/(.{4})/g, "$1 ").trim()}</code></p>}
      <CodeInput id="mfa-enrol-code" label="Código de 6 dígitos" value={code} onChange={setCode} />
      <FormError messages={error} />
      <button className="primary-button full" type="submit" disabled={busy || !secret}>
        <ButtonLabel busy={busy} busyLabel="Verificando" label="Activar verificación" icon={<ShieldCheck size={16} aria-hidden="true" />} />
      </button>
      {footer}
    </form>
  );
}

function RecoveryCodes({ codes, onContinue }: { codes: string[]; onContinue(): void }) {
  const [saved, setSaved] = useState(false);
  const download = () => {
    const blob = new Blob([`Aventuria · códigos de recuperación\n\n${codes.join("\n")}\n\nCada código funciona una sola vez.\n`], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "aventuria-codigos-de-recuperacion.txt";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <div className="auth-form">
      <h2 className="partner-login-title">Guarda tus códigos de recuperación</h2>
      <p className="field-hint">Si pierdes tu teléfono, cada uno de estos códigos te deja entrar una sola vez. Guárdalos en un lugar seguro; no volveremos a mostrarlos.</p>
      <ol className="mfa-codes" translate="no">{codes.map((code) => <li key={code}><code>{code}</code></li>)}</ol>
      <button type="button" className="secondary-button full" onClick={download}>Descargar códigos</button>
      <label className="mfa-confirm">
        <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} /> Ya guardé mis códigos
      </label>
      <button type="button" className="primary-button full" disabled={!saved} onClick={onContinue}>
        <ButtonLabel busy={false} busyLabel="" label="Continuar" icon={<ArrowRight size={16} aria-hidden="true" />} />
      </button>
    </div>
  );
}

function VerifyForm({ email, onDone, footer }: { email: string; onDone(): void; footer: ReactNode }) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string[] | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const body = useRecovery ? { recoveryCode: value.trim() } : { code: value.replace(/\s/g, "") };
      await apiRequest("/api/v1/auth/mfa/verify", { method: "POST", body: JSON.stringify(body) });
      onDone();
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos verificar el código."));
    } finally { setBusy(false); }
  };

  return (
    <form className="auth-form" onSubmit={submit}>
      <h2 className="partner-login-title">Ingresa tu código</h2>
      {useRecovery
        ? <p className="field-hint">Escribe uno de tus códigos de recuperación. Cada uno funciona una sola vez.</p>
        : (
          // Each account has its own authenticator entry, all named "Aventuria": someone with an admin and a
          // lending-partner account must pick the one labelled with this email (client report, 22 Oct 2026).
          <p className="field-hint">
            Abre tu aplicación autenticadora y escribe el código de la entrada <strong>Aventuria</strong> de <strong translate="no">{email}</strong>.
            Si tienes más de una cuenta de Aventuria, cada una tiene su propio código.
          </p>
        )}
      {useRecovery ? (
        <div className="field-block">
          <div className="label-row"><label htmlFor="mfa-recovery">Código de recuperación</label></div>
          <div className="auth-input-wrap">
            <KeyRound size={16} aria-hidden="true" />
            <input id="mfa-recovery" value={value} onChange={(event) => setValue(event.target.value)} autoComplete="off" required autoFocus translate="no" />
          </div>
        </div>
      ) : (
        <CodeInput id="mfa-code" label="Código de 6 dígitos" value={value} onChange={setValue} />
      )}
      <FormError messages={error} />
      <button className="primary-button full" type="submit" disabled={busy}>
        <ButtonLabel busy={busy} busyLabel="Verificando" label="Verificar" icon={<ShieldCheck size={16} aria-hidden="true" />} />
      </button>
      <button type="button" className="link-button" onClick={() => { setUseRecovery((current) => !current); setValue(""); setError(null); }}>
        {useRecovery ? "Usar el código de mi aplicación" : "No tengo mi teléfono: usar un código de recuperación"}
      </button>
      {footer}
    </form>
  );
}
